import CONVERSION_FACTORS from "../../../assets/weight-conversion-factors.json";
import COUNTRYCODES from "../../../assets/country-codes.json";
import {
  CUSTOM_PACKAGE_I,
  FLAT_RATE_PACKAGE_I,
  SESSION_AUTH,
} from "../../../constants/types";
import moment from "moment";
import * as ROUTES from "../../../constants/routes";
import { parsePhoneNumber } from "awesome-phonenumber";

export const convertToWeightUnit = (value, fromUnit, toUnit) => {
  return (
    parseFloat(value) *
    CONVERSION_FACTORS[fromUnit.toString().toLowerCase().trim()][
      toUnit.toString().toLowerCase().trim()
    ]
  );
};

export const getTotalWeightInUnit = (parcels, toUnit) => {
  let totalWeight = 0;
  if (parcels?.parcels?.length > 0) {
    parcels.parcels.forEach((item) => {
      const quantity = parseInt(item.quantity) || 1;
      totalWeight += convertToWeightUnit(
        parseFloat(item.weight) * quantity,
        item.weight_unit,
        toUnit,
      );
    });
  } else if (parcels.selectedWeight?.weight) {
    const quantity = parseInt(parcels.selectedParcel?.quantity) || 1;
    totalWeight = convertToWeightUnit(
      parseFloat(parcels.selectedWeight.weight) * quantity,
      parcels.selectedWeight.weight_unit,
      toUnit,
    );
  }

  return totalWeight;
};

export const missingDetails = (details) => {
  let missing = false;
  if (!details || Object.keys(details).length === 0) {
    return true;
  }
  const businessDetailFields = [
    "country",
    "bank_name",
    "account_number",
    "account_name",
    "document_type",
    "document_name",
    "document_size",
    "tax_number",
  ];
  businessDetailFields.forEach((field, idx) => {
    let item = details[field];
    if ((field !== "is_verified" && !item) || item.toString().trim() === "") {
      missing = true;
    }
  });
  return missing;
};

export const validatePackage = (
  selectedParcel,
  selectedWeight,
  packageType,
) => {
  let valid = true;
  const { length, width, height } = selectedParcel;
  const errors = {};
  let text = "";
  if (packageType === CUSTOM_PACKAGE_I) {
    if (!length || length <= 0) errors.length = true;
    if (!width || width <= 0) errors.width = true;
    if (!height || height <= 0) errors.height = true;
    if (errors.length || errors.width || errors.height) {
      text += "Dimensions";
      valid = false;
    }
  } else if (packageType === FLAT_RATE_PACKAGE_I && !selectedParcel.template) {
    errors.template = true;
    text += "Dimensions";
    valid = false;
  }
  if (!selectedWeight.weight || selectedWeight.weight <= 0) {
    errors.weight = true;
    text += text ? " and Weight" : "Weight";
    valid = false;
  }

  return { valid, errors, message: `Please enter a valid ${text} value` };
};

export const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsDataURL(file);
  });
};

export const isDomesticKenyanShipment = (
  address_from,
  address_to,
  pickupPoint = null,
) => {
  if (doesPickupPointMatchSender(address_from, pickupPoint)) {
    return true;
  }
  if (!address_from?.country || !address_to?.country) return false;
  return address_from.country === "KE" && address_to.country === "KE";
};

const normalizeString = (value) => value?.toString().trim().toLowerCase() || "";

export const isPickupSelected = (pickupPoint) => Boolean(pickupPoint?.id);

export const isSenderEligibleForPickup = (sender) => {
  return (
    normalizeString(sender?.country) === "ke" &&
    normalizeString(sender?.city) === "nairobi"
  );
};

export const doesPickupPointMatchSender = (sender, pickupPoint) => {
  if (!isPickupSelected(pickupPoint)) {
    return false;
  }

  const senderCountry = normalizeString(sender?.country);
  const senderCity = normalizeString(sender?.city);
  const pickupCountry = normalizeString(pickupPoint?.address?.country);
  const pickupCity = normalizeString(pickupPoint?.address?.city);

  return (
    senderCountry &&
    senderCity &&
    senderCountry === pickupCountry &&
    senderCity === pickupCity
  );
};

export const hasPickupCustomerDetails = (receiver) => {
  return Boolean(receiver?.name?.trim() && receiver?.phone?.trim());
};

export const isInternationalShipment = (sender, receiver, pickupPoint) => {
  if (isPickupSelected(pickupPoint)) {
    return false;
  }

  return (
    normalizeString(sender?.country) !== normalizeString(receiver?.country)
  );
};

export const toMoneyFormat = (value, currency) => {
  let options = currency
    ? { style: "currency", currency: currency }
    : { maximumSignificantDigits: 3 };
  let formatter = new Intl.NumberFormat("en-US", options);
  return formatter.format(value);
};

export const getQuantity = (item) => {
  if (!item) return 1;
  const feature = item.variation?.features?.find(
    (feature) => feature.feature_type == "quantity",
  );
  if (feature) {
    return parseInt(feature.value) * parseInt(item.quantity);
  }
  return item.quantity;
};

export const tableDateFormatter = (cell) => {
  const key = "date-column-value";
  return !cell ? (
    <div key={key}>-</div>
  ) : (
    <p
      key={key}
    >{`${moment(new Date(cell)).format("MMM D, YYYY [\r\n] hh:mm A")}`}</p>
  );
};

export const dateFilter = (cell, row) => {
  return !cell
    ? null
    : `${moment(new Date(cell)).format("MMM D, YYYY hh:mm A")}`;
};

export const getAddress = (address) => {
  const street2 = `${address.street2 ? address.street2 + ", " : ""}`;
  return `${address.street1}, ${street2}${address.city}, ${address.zip_code}, ${address.country}`;
};

const levenshteinDistance = (a, b) => {
  const dp = Array.from({ length: a.length + 1 }, (_, i) =>
    Array(b.length + 1).fill(0),
  );
  for (let i = 0; i <= a.length; i++) dp[i][0] = i;
  for (let j = 0; j <= b.length; j++) dp[0][j] = j;

  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      if (a[i - 1] === b[j - 1]) dp[i][j] = dp[i - 1][j - 1];
      else
        dp[i][j] = Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]) + 1;
    }
  }
  return dp[a.length][b.length];
};

export const findBestMatch = (searchString, strings) => {
  return strings.reduce(
    (best, current) => {
      const distance = levenshteinDistance(searchString, current);
      return distance < best.distance ? { value: current, distance } : best;
    },
    { value: null, distance: Infinity },
  ).value;
};

const routeToRuleMapping = {
  [ROUTES.DASHBOARD]: process.env?.REACT_APP_DASHBOARD_REDIRECT ?? "dev",
  [ROUTES.WALLETS]: process.env?.REACT_APP_WALLET_REDIRECT ?? "dev",
  [ROUTES.MANIFESTS]: process.env?.REACT_APP_MANIFEST_REDIRECT ?? "dev",
  [ROUTES.LABEL]: process.env?.REACT_APP_LABELS_REDIRECT ?? "dev",
  [ROUTES.ORDERS]: process.env?.REACT_APP_ORDERS_REDIRECT ?? "dev",
  [ROUTES.STORES]: process.env?.REACT_APP_STORES_REDIRECT ?? "dev",
  [ROUTES.ACCOUNT]: process.env?.REACT_APP_SETTINGS_REDIRECT ?? "dev",
  [ROUTES.HELP_CENTER]: process.env?.REACT_APP_HELP_CENTER_REDIRECT ?? "dev",
};

export const isBlockedWalletAccount = (authType, email) => {
  const blockedWalletAccounts =
    process.env.REACT_APP_BLOCKED_WALLET_ACCOUNTS?.toString().split(";") || [];

  return authType !== SESSION_AUTH && blockedWalletAccounts.includes(email);
};

export const isAllowedRedirect = (route) => {
  const ENV = process.env?.REACT_APP_ENV ? process.env.REACT_APP_ENV : "local";
  const getAllowedEnv = routeToRuleMapping[route];

  if (getAllowedEnv === "none") {
    return false;
  }
  if (getAllowedEnv === "local") {
    return !ENV || !["production", "development", "preview"].includes(ENV);
  }

  return ENV !== "production" || getAllowedEnv === "prod";
};

const routeToNewRouteMapping = {
  [ROUTES.DASHBOARD]: "",
  [ROUTES.WALLETS]: "wallet",
  [ROUTES.MANIFESTS]: "manifests",
  [ROUTES.LABEL]: "labels",
  [ROUTES.ORDERS]: "orders",
  [ROUTES.STORES]: "stores",
  [ROUTES.ACCOUNT]: "settings",
  [ROUTES.HELP_CENTER]: "help-center",
};

function getDashboardBaseUrl() {
  return process.env?.REACT_APP_DASHBOARD_URL
    ? process.env.REACT_APP_DASHBOARD_URL
    : `http://localhost:3000`;
}

export const getRedirectURL = (route, webToken, authType, organizationId) => {
  const newDashboardURL = getDashboardBaseUrl();
  let tokenParam = "";
  let organizationParam = "";
  if (authType !== SESSION_AUTH) {
    tokenParam = `&webToken=${webToken}`;
  }
  if (organizationId) {
    organizationParam = `/${organizationId}`;
  }
  const redirectURL = `${newDashboardURL}/api${organizationParam}/redirect?redirectTo=${routeToNewRouteMapping[route]}${tokenParam}`;
  if (isAllowedRedirect(route) && (authType === SESSION_AUTH || webToken)) {
    return redirectURL;
  }
  return route;
};

export const BUILDER_NEXT = "builder";
const BUILDER_NEXT_STORAGE_KEY = "shipshap.next";

export const persistBuilderNextFromSearch = (search) => {
  try {
    const query = search.startsWith("?") ? search.slice(1) : search;
    const params = new URLSearchParams(query);
    if (params.get("next") === BUILDER_NEXT) {
      sessionStorage.setItem(BUILDER_NEXT_STORAGE_KEY, BUILDER_NEXT);
    } else {
      sessionStorage.removeItem(BUILDER_NEXT_STORAGE_KEY);
    }
  } catch (error) {
    console.error("Unable to persist builder login next token", error);
  }
};

export const hasBuilderNext = () => {
  try {
    return sessionStorage.getItem(BUILDER_NEXT_STORAGE_KEY) === BUILDER_NEXT;
  } catch (error) {
    console.error("Unable to read builder login next token", error);
    return false;
  }
};

export const consumeBuilderNext = () => {
  const present = hasBuilderNext();
  try {
    sessionStorage.removeItem(BUILDER_NEXT_STORAGE_KEY);
  } catch (error) {
    console.error("Unable to clear builder login next token", error);
  }
  return present;
};

export const getBuilderRedirectURL = (webToken, authType) => {
  let query = `next=${BUILDER_NEXT}`;
  if (authType !== SESSION_AUTH && webToken) {
    query += `&webToken=${webToken}`;
  }
  return `${getDashboardBaseUrl()}/api/redirect?${query}`;
};

export const isPublicHoliday = () => {
  const publicHolidayAlertDays =
    process.env?.REACT_APP_PUBLIC_HOLIDAY_ALERT_DAYS?.toString().split(";") ||
    [];

  const isPublicHoliday = publicHolidayAlertDays.some((date) => {
    const [day, month] = date.split("/").map(Number);
    const currentMonth = new Date().getMonth() + 1;
    const currentDay = new Date().getDate();
    return month === currentMonth && day === currentDay;
  });
  return isPublicHoliday;
};

const VALID_ALERT_TYPES = ["info", "warning", "danger"];
const DEFAULT_ALERT_TYPE = "danger";

const isAlertActive = (alert) => {
  if (!alert || typeof alert !== "object") return false;
  if (
    !alert.message ||
    typeof alert.message !== "string" ||
    !alert.message.trim()
  )
    return false;
  if (alert.hidden === true) return false;

  const type = alert.type ?? DEFAULT_ALERT_TYPE;
  if (!VALID_ALERT_TYPES.includes(type)) return false;

  if (alert.expiresAt == null) return true;

  const expiry = new Date(alert.expiresAt.toString().trim());
  if (isNaN(expiry.getTime())) return false;

  return expiry > new Date();
};

export const getActiveDashboardAlerts = () => {
  const jsonAlerts = process.env?.REACT_APP_DASHBOARD_ALERTS;
  if (!jsonAlerts) return [];
  try {
    const parsed = JSON.parse(jsonAlerts);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isAlertActive).map((alert) => ({
      message: alert.message.trim(),
      type: VALID_ALERT_TYPES.includes(alert.type)
        ? alert.type
        : DEFAULT_ALERT_TYPE,
    }));
  } catch {
    return [];
  }
};

export const isValidParsedPhoneNumber = (phoneNumberString) => {
  let phoneNumber = phoneNumberString.toString().trim();
  if (phoneNumber.startsWith("+")) {
    phoneNumber = phoneNumber.substring(1);
  }
  const parsed = parsePhoneNumber(`+${phoneNumber}`);
  if (parsed.valid) {
    return true;
  }

  // awesome-phonenumber v7 reports regionCode, not country. Newer Safaricom
  // 01xx mobiles (0141–0149) are possible KE numbers but not yet marked valid.
  // Both Kenyan mobile shapes are 9 national digits: 07xx… and 01xx….
  const nationalNumber = parsed.number?.significant || "";
  return (
    parsed.regionCode === "KE" &&
    parsed.possible &&
    /^[17]\d{8}$/.test(nationalNumber)
  );
};

export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.toString().trim() === "") {
    return false;
  }

  if (!isValidParsedPhoneNumber(phoneNumber)) {
    return false;
  }

  return !Object.values(COUNTRYCODES).some((item) => {
    return (
      item.dial_code === phoneNumber || item.dial_code === `+${phoneNumber}`
    );
  });
};
